import { Chip } from '@biq/apex-ui';
import { ReleaseNotesIcon } from '../../assets';
import { themeObject } from '../../lib';
import { toLocalDateTimeString } from 'lib';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

export type SystemMessageType = 'GENERAL' | 'RELEASE_NOTES' | 'ALERT';

export interface SystemMessageData {
    messageId: string;
    messageType: SystemMessageType;
    title: string;
    createDate: string;
    contentBase64: string;
}

export const daysOfMessagesDropdownData = [
    {
        daysName: 'Last 15 Days',
        daysValue: '15',
    },
    {
        daysName: 'Last 30 Days',
        daysValue: '30',
    },
    {
        daysName: 'Last 60 Days',
        daysValue: '60',
    },
    {
        daysName: 'Last 90 Days',
        daysValue: '90',
    },
    {
        daysName: 'View All',
        daysValue: 'ALL',
    },
];

export const styles = {
    mainContainer: {
        display: 'flex',
        flexDirection: 'column',
        p: '24px 31px 102px 31px',
        height: '100%',
    },
    titleContainerStyles: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        '& .refresh-icon': {
            color: themeObject.palette.primary[500],
        },
    },
    headingTextStyles: {
        fontFamily: 'Raleway',
        fontSize: '28px',
        fontWeight: 600,
        lineHeight: '32.87px',
        color: themeObject.palette.primary[700],
        mr: '4px',
    },
    searchContainerStyles: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mt: '20px',
    },
    emptyStateContainerStyles: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        mt: '20px',
    },
    emptyHeadingTextStyles: {
        ...themeObject.typography.h2,
        fontSize: '26px',
        lineHeight: '30.52px',
        color: themeObject.palette.primary[700],
    },
    emptySubHeadingTextStyles: {
        ...themeObject.typography.body.default,
        ...themeObject.typography.body.body1,
        ...themeObject.typography.body.regular,
        lineHeight: '21.17px',
        color: themeObject.palette.grayScale[700],
    },
    searchFieldStyles: {
        width: '284px',
    },
    selectFieldStyles: {
        width: '220px',
    },
};

export const getChipColorAndText = (
    type: SystemMessageType,
    formattedIcon?: boolean
): {
    label: string;
    color:
        | 'default'
        | 'secondary'
        | 'primary'
        | 'success'
        | 'error'
        | 'info'
        | 'warning'
        | 'grayscale';
    icon: any;
} => {
    if (type === 'GENERAL') {
        return {
            label: 'General',
            color: 'grayscale',
            icon: (
                <EmailOutlinedIcon
                    style={
                        formattedIcon
                            ? {
                                  color: themeObject.palette.tertiary
                                      .purple[400],
                                  height: '20px',
                                  width: '20px',
                              }
                            : {}
                    }
                />
            ),
        };
    } else if (type === 'RELEASE_NOTES') {
        return {
            label: 'Release Notes',
            color: 'primary',
            icon: formattedIcon ? (
                <DescriptionOutlinedIcon
                    style={{
                        color: themeObject.palette.primary[500],
                        height: '20px',
                        width: '20px',
                    }}
                />
            ) : (
                <img
                    src={ReleaseNotesIcon}
                    alt='release-notes-icon'
                    style={{ width: '18px', height: '18px' }}
                />
            ),
        };
    } else if (type === 'ALERT') {
        return {
            label: 'Alert',
            color: 'error',
            icon: (
                <ErrorOutlineIcon
                    style={
                        formattedIcon
                            ? {
                                  color: themeObject.palette.tertiary.red[300],
                                  height: '20px',
                                  width: '20px',
                              }
                            : {}
                    }
                />
            ),
        };
    }
    return {
        label: 'Alert',
        color: 'error',
        icon: (
            <ErrorOutlineIcon
                style={
                    formattedIcon
                        ? {
                              color: themeObject.palette.tertiary.red[300],
                              height: '20px',
                              width: '20px',
                          }
                        : {}
                }
            />
        ),
    };
};

export const getChipComponent = (
    messageType: SystemMessageType
): React.JSX.Element => {
    const { label, color, icon } = getChipColorAndText(messageType);
    return (
        <Chip
            label={label}
            size='small'
            variant='filled'
            color={color}
            icon={icon}
            readOnly={true}
        />
    );
};

type Column = {
    field: string;
    headerName: string;
    flex?: number;
    width?: number;
    renderCell?: (params: any) => any;
};

export const columns: Column[] = [
    {
        field: 'createDate',
        headerName: 'Date',
        width: 183,
        renderCell: (params: any): string => {
            return toLocalDateTimeString(params.row?.createDate, true);
        },
    },
    {
        field: 'messageType',
        width: 208,
        headerName: 'Type',
        renderCell: (params) => getChipComponent(params.row?.messageType),
    },
    {
        field: 'title',
        headerName: 'Title',
        flex: 1,
    },
];
